import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GenericModal } from "./modal";
import { LOCALES } from "../../common/constants";
import { LandingPopup, LandingPopupMobile } from "./../components/images/png/icons";
import "./landing-pop-up.style.scss";
import { useEffect, useState } from "react";
import { readCookie } from "../../common/FunctionUtils";

interface Feature {
    title: string;
    description: string;
}

interface AdSenseProps {
    isShow: boolean;
    setIsShow: React.Dispatch<React.SetStateAction<boolean>>;
    modalSize?: "sm" | "lg" | "xl";
    showCloseButton?: boolean;
    closeOnBackdrop?: boolean;
    features?: Feature[];
    customImages?: {
        desktop: string;
        mobile: string;
        alt: string;
    };
    customTitles?: {
        desktop: string;
        mobile: string;
    };
    customSubtitles?: {
        desktop: string;
        mobile: string;
    };
    customButtonText?: string;
    onCreateAccount?: () => void;
    enableDefaultNavigation?: boolean;
    className?: string;
}

export const AdSense = ({
    setIsShow,
    modalSize = "lg",
    showCloseButton = false,
    closeOnBackdrop = true,
    features,
    customImages,
    customTitles,
    customSubtitles,
    customButtonText,
    onCreateAccount,
    enableDefaultNavigation = true,
    className = ""
}: AdSenseProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const language = localStorage.getItem("i18nextLng");
    const [isVisible, setIsVisible] = useState(
        readCookie("_cc_cookieConsent") ? true : false
    );
    useEffect(() => {
        const pathname = window.location.pathname;
        const protectedRoutes = [
            "/e-postregister",
            "/emailregister",
            "/login",
            "/logga-in",
            "/emaillogin",
            "/e-postinloggning",
            "/contact-us",
            "/kontakta-oss"
        ];
        const shouldHide = protectedRoutes.includes(pathname) || pathname.includes("dashboard") || pathname.includes("instrumentbrada");
        if (shouldHide) {
            setIsShow(false);
        }
    }, [setIsShow]);

    const defaultFeatures: Feature[] = [
        {
            title: t("NO_BINDING_PERIOD"),
            description: t("NO_BINDING_PERIOD_DESC")
        },
        {
            title: t("WE_HANDLE_ADMINISTRATIVE_TASKS"),
            description: t("WE_HANDLE_ADMINISTRATIVE_TASKS_DESC")
        },
        {
            title: t("NO_FIXED_MONTHLY_FEE"),
            description: t("NO_FIXED_MONTHLY_FEE_DESC")
        },
        {
            title: t("PROFESSIONAL_SUPPORT"),
            description: t("PROFESSIONAL_SUPPORT_DESC")
        }
    ];

    const defaultImages = {
        desktop: LandingPopup,
        mobile: LandingPopupMobile,
        alt: "Invozio - Välj oss för enkel fakturering"
    };

    const featuresData = features || defaultFeatures;
    const imagesData = customImages || defaultImages;

    const handleCreateAccount = () => {
        try {
            if (onCreateAccount) {
                onCreateAccount();
            } else if (enableDefaultNavigation) {
                navigate(
                    language === 'en-US'
                        ? `/emailregister?lang=en-US`
                        : `/e-postregister?lang=${LOCALES.SV_SE}`
                );
            }
            setIsShow(false);
        } catch (error) {
            console.error('Error handling create account:', error);
            setIsShow(false);
        }
    };
      useEffect(() => {
        if (!isVisible) {
          let interval: NodeJS.Timeout;
          const checkCookies = () => {
            const isConsentCookie = readCookie("_cc_cookieConsent");
    
            if (isConsentCookie) {
              if (interval) {
                clearInterval(interval);
              }
              setTimeout(() => {
                setIsShow(true);
              }, 60000);
            }
          };
          checkCookies();
          interval = setInterval(checkCookies, 1000);
          return () => clearInterval(interval);
        }
      }, []);

    const getTitle = (isMobile: boolean): string => {
        if (customTitles) {
            return isMobile ? customTitles.mobile : customTitles.desktop;
        }
        return isMobile ? t("CHOOSE_THE_INVOZIO") : t("CHOOSE_INVOZIO");
    };

    const getSubtitle = (isMobile: boolean): string => {
        if (customSubtitles) {
            return isMobile ? customSubtitles.mobile : customSubtitles.desktop;
        }
        return isMobile ? t('INVOICE_FLEXIBLE_PAYMENT_PROMO') : t("SIMPLE_INVOCING_WITHOUT_HIDDEN_CHARGE");
    };

    const getButtonText = (): string => {
        return customButtonText || t("GET_STARTED");
    };

    return (
        <div className="d-flex justify-content-center">
            <GenericModal
                show={isVisible}
                onClose={() => setIsVisible(false)}
                size={modalSize}
                showCloseButton={showCloseButton}
                closeOnBackdrop={closeOnBackdrop}
                showFooter={false}
                className={`landing-popup-modal ${className}`}
            >
                <button
                    type="button"
                    className="position-absolute right-2 top-5 d-block mb-2 z-3"
                    aria-label="Close"
                    onClick={() => setIsShow(false)}
                    style={{ zIndex: 1056 }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 2L14 14M14 2L2 14" stroke="#252B37" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                </button>

                <div className="landing-popup-container p-3 mt-sm-0">
                    <div className="row g-0">
                        <div className="col-lg-5 col-md-6 col-12 d-sm-flex px-0">
                            <img
                                src={imagesData.desktop}
                                alt={imagesData.alt}
                                className="img-fluid  pop-up-image   h-100 h d-sm-block d-none"
                                style={{ objectFit: "cover", height: "100%" }}
                            />
                            <img
                                src={imagesData.mobile}
                                alt={imagesData.alt}
                                className="img-fluid pt-4 mx-auto d-sm-none d-block px-0 mx-0"
                            />
                        </div>

                        <div className="col-lg-7 col-md-6 col-12 d-flex flex-column justify-content-start align-items-start px-0 px-sm-4 py-0 py-sm-4">
                            {/* Desktop View */}
                            <div className="landing-popup-content d-none d-sm-block">
                                <h2 className="popup-title">
                                    {getTitle(false)}
                                </h2>
                                <p className="popup-subtitle text-start">
                                    {getSubtitle(false)}
                                </p>

                                <ul className="popup-features-list">
                                    {featuresData.map((feature, index) => (
                                        <li key={index} className="popup-feature-item">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                                <path d="M16 4.5C16.5523 4.5 17 4.05228 17 3.5C17 2.94772 16.5523 2.5 16 2.5V4.5ZM22 12.5C22 11.9477 21.5523 11.5 21 11.5C20.4477 11.5 20 11.9477 20 12.5H22ZM5.18404 21.064L5.63803 20.173H5.63803L5.18404 21.064ZM3.43597 19.316L4.32698 18.862L4.32698 18.862L3.43597 19.316ZM18.816 21.064L18.362 20.173H18.362L18.816 21.064ZM20.564 19.316L19.673 18.862V18.862L20.564 19.316ZM3.43597 5.68404L2.54497 5.23005L2.54497 5.23005L3.43597 5.68404ZM5.18404 3.93597L4.73005 3.04497L4.73005 3.04497L5.18404 3.93597ZM12.4173 14.1427L11.7077 13.4381L11.7077 13.4381L12.4173 14.1427ZM21.7096 6.20464C22.0987 5.81276 22.0965 5.17959 21.7046 4.79043C21.3128 4.40127 20.6796 4.40348 20.2904 4.79536L21.7096 6.20464ZM11.7077 14.1427L12.4173 13.4381L12.4173 13.4381L11.7077 14.1427ZM8.70957 9.70445C8.32041 9.31257 7.68724 9.31036 7.29536 9.69952C6.90348 10.0887 6.90127 10.7218 7.29043 11.1137L8.70957 9.70445ZM14.6 21.5V20.5H9.4V21.5V22.5H14.6V21.5ZM3 15.1H4V9.9H3H2V15.1H3ZM9.4 3.5V4.5H16V3.5V2.5H9.4V3.5ZM21 12.5H20V15.1H21H22V12.5H21ZM9.4 21.5V20.5C8.26339 20.5 7.47108 20.4992 6.85424 20.4488C6.24907 20.3994 5.90138 20.3072 5.63803 20.173L5.18404 21.064L4.73005 21.955C5.32234 22.2568 5.96253 22.3826 6.69138 22.4422C7.40855 22.5008 8.2964 22.5 9.4 22.5V21.5ZM3 15.1H2C2 16.2036 1.99922 17.0914 2.05782 17.8086C2.11737 18.5375 2.24318 19.1777 2.54497 19.77L3.43597 19.316L4.32698 18.862C4.19279 18.5986 4.10062 18.2509 4.05118 17.6458C4.00078 17.0289 4 16.2366 4 15.1H3ZM5.18404 21.064L5.63803 20.173C5.07354 19.8854 4.6146 19.4265 4.32698 18.862L3.43597 19.316L2.54497 19.77C3.02433 20.7108 3.78924 21.4757 4.73005 21.955L5.18404 21.064ZM14.6 21.5V22.5C15.7036 22.5 16.5914 22.5008 17.3086 22.4422C18.0375 22.3826 18.6777 22.2568 19.27 21.955L18.816 21.064L18.362 20.173C18.0986 20.3072 17.7509 20.3994 17.1458 20.4488C16.5289 20.4992 15.7366 20.5 14.6 20.5V21.5ZM21 15.1H20C20 16.2366 19.9992 17.0289 19.9488 17.6458C19.8994 18.2509 19.8072 18.5986 19.673 18.862L20.564 19.316L21.455 19.77C21.7568 19.1777 21.8826 18.5375 21.9422 17.8086C22.0008 17.0914 22 16.2036 22 15.1H21ZM18.816 21.064L19.27 21.955C20.2108 21.4757 20.9757 20.7108 21.455 19.77L20.564 19.316L19.673 18.862C19.3854 19.4265 18.9265 19.8854 18.362 20.173L18.816 21.064ZM3 9.9H4C4 8.76339 4.00078 7.97108 4.05118 7.35424C4.10062 6.74907 4.19279 6.40138 4.32698 6.13803L3.43597 5.68404L2.54497 5.23005C2.24318 5.82234 2.11737 6.46253 2.05782 7.19138C1.99922 7.90855 2 8.7964 2 9.9H3ZM9.4 3.5V2.5C8.2964 2.5 7.40855 2.49922 6.69138 2.55782C5.96253 2.61737 5.32234 2.74318 4.73005 3.04497L5.18404 3.93597L5.63803 4.82698C5.90138 4.69279 6.24907 4.60062 6.85424 4.55118C7.47108 4.50078 8.26339 4.5 9.4 4.5V3.5ZM3.43597 5.68404L4.32698 6.13803C4.6146 5.57354 5.07354 5.1146 5.63803 4.82698L5.18404 3.93597L4.73005 3.04497C3.78924 3.52433 3.02433 4.28924 2.54497 5.23005L3.43597 5.68404ZM12.4173 14.1427L13.1268 14.8474L21.7096 6.20464L21 5.5L20.2904 4.79536L11.7077 13.4381L12.4173 14.1427ZM11.7077 14.1427L12.4173 13.4381L8.70957 9.70445L8 10.4091L7.29043 11.1137L10.9982 14.8474L11.7077 14.1427ZM12.4173 14.1427L11.7077 13.4381C11.9033 13.2412 12.2217 13.2412 12.4173 13.4381L11.7077 14.1427L10.9981 14.8474C11.5848 15.4381 12.5402 15.4381 13.1269 14.8474L12.4173 14.1427Z" fill="#6750B8" />
                                            </svg>
                                            <div className="feature-content-pop-up">
                                                <h6 className="feature-title py-0">
                                                    {feature.title}
                                                </h6>
                                                <p className="feature-description-pop-up">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                <div className="popup-cta-section">
                                    <button
                                        className="popup-cta-button w-75 mx-2 mt-3"
                                        onClick={handleCreateAccount}
                                    >
                                        {getButtonText()}
                                        <span className="mx-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
                                                <path d="M1.16667 6H12M7.83333 1L12.2441 5.41074C12.5695 5.73618 12.5695 6.26382 12.2441 6.58926L7.83333 11" stroke="white" strokeWidth="1.67" strokeLinecap="round" />
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Mobile View */}
                            <div className="d-block d-sm-none">
                                <h2 className="feature-title text-start">
                                    {getTitle(true)}
                                </h2>
                                <div className="feature-content-pop-up">
                                    <p className="popup-subtitle text-start my-0 py-0">
                                        {getSubtitle(true)}
                                    </p>
                                    <div className="popup-cta-section pt-4 pt-sm-0 mt-0">
                                        <button
                                            className="popup-cta-button w-100 m-0 py-2 px-3"
                                            onClick={handleCreateAccount}
                                        >
                                            {getButtonText()}
                                            <span className="mx-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
                                                    <path d="M1.16667 6H12M7.83333 1L12.2441 5.41074C12.5695 5.73618 12.5695 6.26382 12.2441 6.58926L7.83333 11" stroke="white" strokeWidth="1.67" strokeLinecap="round" />
                                                </svg>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </GenericModal>
        </div>

    );
};